import React, { useEffect, useRef, useState } from "react";
import { Modal, Icon } from "semantic-ui-react";
import Slider from "react-slick";
import { Photo } from "../../types/siteTypes";
import {
    SampleNextArrow,
    SamplePrevArrow,
} from "../../utils/reusableFunctions";
import styles from "./ImageLightbox.module.scss";

type Props = {
    photos: Photo[];
    isOpen: boolean;
    onClose: () => void;
    initialSlide?: number;
};

const ImageLightbox: React.FC<Props> = ({
    photos,
    isOpen,
    onClose,
    initialSlide = 0,
}) => {
    const [currentSlide, setCurrentSlide] = useState(initialSlide);
    const sliderRef = useRef<Slider>(null);

    useEffect(() => {
        if (isOpen && sliderRef.current) {
            sliderRef.current.slickGoTo(initialSlide);
            setCurrentSlide(initialSlide);
        }
    }, [isOpen, initialSlide]);

    const settings = {
        dots: true,
        infinite: photos.length > 1,
        speed: 500,
        slidesToShow: 1,
        slidesToScroll: 1,
        nextArrow: <SampleNextArrow />,
        prevArrow: <SamplePrevArrow />,
        beforeChange: (_current: number, next: number) => setCurrentSlide(next),
        initialSlide: initialSlide,
    };

    return (
        <Modal
            open={isOpen}
            onClose={onClose}
            className={styles.lightbox}
            closeIcon
            size="fullscreen"
        >
            <Modal.Content className={styles.content}>
                <div className={styles.header}>
                    <span className={styles.counter}>
                        {currentSlide + 1} / {photos.length}
                    </span>
                    <Icon
                        name="close"
                        size="large"
                        className={styles.closeButton}
                        onClick={onClose}
                    />
                </div>
                <div className={styles.sliderContainer}>
                    <Slider {...settings} ref={sliderRef}>
                        {photos.map((photo, index) => (
                            <div key={`lightbox-${index}`} className={styles.slide}>
                                <div className={styles.imageWrapper}>
                                    <img
                                        src={photo.url}
                                        alt={photo.title || `Image ${index + 1}`}
                                        className={styles.image}
                                    />
                                </div>
                                {photo.title && (
                                    <div className={styles.caption}>{photo.title}</div>
                                )}
                                {photo.description && (
                                    <div className={styles.description}>{photo.description}</div>
                                )}
                            </div>
                        ))}
                    </Slider>
                </div>
            </Modal.Content>
        </Modal>
    );
};

export default ImageLightbox;
